import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "./Label.scss";
import { deleteLabel, getLabelsByUser } from "../../services/api/LabelsApi.js";
import { BiEdit, BiTrash } from "react-icons/bi";
import { useLoading } from "../../context/LoadingContext.js";
import { useToast } from "../../context/ToastContext";
import { useNavigate } from "react-router-dom";

import { IoGrid } from "react-icons/io5";
import { FaTableList } from "react-icons/fa6";

import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal.js";
import { useSearchResults } from "../../context/SearchResultsContext.js";
import { WrittenFormat } from "../../components/WrittenFormat.js";
import { getBothCourtCasesDetailsByCourtRef } from "../../services/api/CasesApi.js";
import { useLabelSelection } from "../../context/LabelSelectionContext.js";
import { formatText } from "../../components/TruncateParties.js";

export const Labels = () => {
  const [labels, setLabels] = useState([]);
  const [caseData, setCaseData] = useState({});

  const [viewMode, setViewMode] = useState("table");

  const { searchResults, searchType } = useSearchResults();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [labelToDelete, setLabelToDelete] = useState(null);

  const { showLoading, hideLoading } = useLoading();
  const [error, setError] = useState(null);
  const { showToast } = useToast();

  const {
    searchTerm: labelSearchTerm,
    setSearchTerm: setLabelSearchTerm,
    filters,
    setFilters,
    filteredLabels,
    setFilteredLabels,
    clearAllFilters: clearLabelFilters,
    hasActiveFilters: hasLabelFilters,
    triggerLabelRefresh,
    updatePaginationInfo,
    pagination,
    setPagination, // ADD THIS
    setIsLoading,
  } = useLabelSelection();

   const [currentPage, setCurrentPage] = useState(0);
  // const [labelsPerPage, setLabelsPerPage] = useState(20);

  const searchInput = labelSearchTerm;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${date.getDate()} ${date.toLocaleString("default", {
      month: "long",
    })} ${date.getFullYear().toString().substr(-2)}`;
  };

  const handleLabelsPerPageChange = useCallback((e) => {
    const newLabelsPerPage = Number(e.target.value);
    setPagination(prev => ({
      ...prev,
      limit: newLabelsPerPage,
      page: 0
    }));
  }, [setPagination]);

  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  }, [setPagination]);

  // Reset to first page when filters change
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      page: 0
    }));
  }, [filters, searchInput, setPagination]);

  useEffect(() => {
    // console.log("Refetching due to pagination/filter change:", {
    //   currentPage,
    //   labelsPerPage,
    //   filters,
    //   searchInput,
    // });

    const refetchOnChange = async () => {
      try {
        showLoading();
        const params = new URLSearchParams();
        params.set("page", pagination.page.toString());
        params.set("limit", pagination.limit.toString());

        // Add search term
        if (searchInput && searchInput.trim()) {
          params.set("search", searchInput.trim());
        }

        // Add all filter parameters with proper empty string checking
        if (filters.name && filters.name.trim() !== "") {
          params.set("name", filters.name.trim());
        }
        if (filters.description && filters.description.trim() !== "") {
          params.set("description", filters.description.trim());
        }
        if (filters.priorityLevel && filters.priorityLevel.trim() !== "") {
          params.set("priorityLevel", filters.priorityLevel.trim());
        }
        if (filters.courtRef && filters.courtRef.trim() !== "") {
          params.set("courtRef", filters.courtRef.trim());
        }
        if (filters.fromDate && filters.fromDate.trim() !== "") {
          params.set("fromDate", filters.fromDate.trim());
        }
        if (filters.toDate && filters.toDate.trim() !== "") {
          params.set("toDate", filters.toDate.trim());
        }

        console.log("Making API call with params:", params.toString());

        const response = await getLabelsByUser(`?${params.toString()}`);
        const data = response.data;

        // Process the labels like before
        const processedLabels = data.labels
          ? data.labels.flatMap((item) =>
            item.labels.map((label) => ({
              _id: label._id,
              name: label.name,
              color: label.color,
              description: label.description,
              priorityLevel: label.priorityLevel,
              createdAt: label.createdAt,
              branchId: label.branchId,
              courtRef: label.courtRef,
              user: label.user,
              labeldata: label,
            }))
          )
          : [];

        setFilteredLabels(processedLabels);

        // Update pagination info if available
        if (data.total !== undefined) {
          updatePaginationInfo(
            data.total,
            data.total > (pagination.page + 1) * pagination.limit
          );
        }
      } catch (err) {
        console.error("Error fetching labels:", err);
        setError(err.message || "Failed to fetch labels.");
      } finally {
        hideLoading();
      }
    };

    refetchOnChange();
  }, [filters, searchInput, pagination.page, pagination.limit]);

  const handleFilterChange = useCallback(
    (field, value) => {
      console.log("Filter changed:", field, "from", filters[field], "to", value);
      setIsLoading(true);

      // Always update the filter, even if it's an empty string
      const newFilters = { ...filters, [field]: value || "" };
      console.log("New filters object:", newFilters);
      setFilters(newFilters);
    },
    [filters, setFilters, setIsLoading]
  );

  const handleExport = () => {
    showToast("Export functionality is not implemented yet.", { type: "info" });
  };

  const navigate = useNavigate();

  const handleDeleteClick = (label) => {
    setLabelToDelete(label);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!labelToDelete) return;

    try {
      showLoading();
      await deleteLabel(labelToDelete._id);


      setLabels((prev) => prev.filter((l) => l._id !== labelToDelete._id));
      triggerLabelRefresh();

      if (filteredLabels.length === 1 && pagination.page > 0) {
      // Go back to the previous page. This will trigger the useEffect to refetch.
      setPagination(prev => ({
        ...prev,
        page: prev.page - 1
      }));
    } else {
      // Otherwise, just refresh the current page's data using the trigger.
      triggerLabelRefresh();
    }
    } catch (err) {
      console.error("Delete error", err);
      showToast("Failed to delete label. Please try again.", { type: "error" });
    } finally {
      hideLoading();
      setIsDeleteModalOpen(false);
      setLabelToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setLabelToDelete(null);
  };

  const handleEdit = (label) => {
    console.log(label, "labeeeelllll");
    navigate(`/home/labels/editlabel/${label._id}`, { state: label });
  };

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const displayLabels = useMemo(() => {
    // Priority 1: If we have search results, use them
    if (searchResults.length > 0 && searchType === "labels") {
      console.log("Using search results");
      return searchResults;
    }

    // Priority 2: Use filtered labels from context
    console.log("Using filtered labels from context");
    return filteredLabels;
  }, [filteredLabels, searchResults, searchType]);

  useEffect(() => {
    const fetchCaseDetails = async (courtRef) => {
      if (!courtRef) return;
      try {
        const response = await getBothCourtCasesDetailsByCourtRef(courtRef);
        setCaseData((prev) => ({
          ...prev,
          [courtRef]: response.data,
        }));
      } catch (err) {
        console.error("Failed to fetch courtRef parties", err);
      }
    };

    displayLabels.forEach((label) => {
      const courtRefs = [
        ...(label.labeldata?.districtCourtCases || []),
        ...(label.labeldata?.highCourtCases || []),
        ...(label.labeldata?.otherCourtCases || []),
        label.courtRef,
      ].filter(Boolean);

      courtRefs.forEach((ref) => {
        if (ref && !caseData[ref]) {
          fetchCaseDetails(ref);
        }
      });
    });
  }, [displayLabels]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".filter-dropdown-wrapper")) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasActiveFilters = Object.values(filters).some((val) => val);
  useEffect(() => {
    // Log all priorityLevel values to check for undefined or case issues
    labels.forEach((label) => {
      console.log(
        "Label:",
        label.name,
        "| priorityLevel:",
        label.priorityLevel
      );
    });
  }, [labels]);

  const filterButtonRef = useRef();
  const filterDropdownRef = useRef();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownOverflow, setDropdownOverflow] = useState("auto");

  const shouldShowNoDataMessage = useMemo(() => {
    return displayLabels.length === 0;
  }, [displayLabels.length]);

  const getNoDataMessage = useMemo(() => {
    if (hasLabelFilters && displayLabels.length === 0) {
      return "No labels found matching your search and filters.";
    }

    if (searchType === "labels" && displayLabels.length === 0) {
      return "No labels found matching your search.";
    }

    return "No labels found. Start by creating a new one!";
  }, [hasLabelFilters, displayLabels.length, searchType]);


  useEffect(() => {
    if (isFilterOpen && filterButtonRef.current) {
      const buttonRect = filterButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 365;
      const dropdownMargin = 4;
      let leftPosition = buttonRect.left;
      if (leftPosition + dropdownWidth > window.innerWidth) {
        leftPosition = buttonRect.right - dropdownWidth;
      }
      if (leftPosition < 0) {
        leftPosition = 10;
      }
      // Always show below the button, but limit maxHeight if needed
      setDropdownPosition({
        top: buttonRect.bottom + dropdownMargin,
        left: leftPosition,
        maxHeight: 300,
      });
    }

    const handleScroll = (e) => {
      if (!isFilterOpen) return;
      // Only close if scroll is outside the dropdown
      const dropdown = document.querySelector(".filter-dropdown-box");
      if (dropdown && e && e.target && dropdown.contains(e.target)) {
        // Scrolling inside the dropdown, do nothing
        return;
      }
      setIsFilterOpen(false);
    };

    if (isFilterOpen) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isFilterOpen]);

  // Check if dropdown content is overflowing and set overflowY accordingly
  useEffect(() => {
    if (isFilterOpen && filterDropdownRef.current) {
      const el = filterDropdownRef.current;
      if (el.scrollHeight > el.clientHeight) {
        setDropdownOverflow("auto");
      } else {
        setDropdownOverflow("visible");
      }
    }
  }, [isFilterOpen, dropdownPosition]);
  return (
    <>
      <div className={`label-main-div-header`}>
        <div className="invoices-header">
          <div className="view-toggle-wrapper">
            <button
              className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""
                }`}
              onClick={() => setViewMode("grid")}
            >
              <IoGrid />
            </button>
            <button
              className={`view-toggle-btn ${viewMode === "table" ? "active" : ""
                }`}
              onClick={() => setViewMode("table")}
            >
              <FaTableList />
            </button>
            <div className="common-pagination-settings">
              <label htmlFor="labelPerPageSelect">Labels per page:</label>
              <select
                id="labelPerPageSelect"
                value={pagination.limit}
                onChange={handleLabelsPerPageChange}
              >
                {[5, 10, 15, 20, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-dropdown-wrapper">
            <button
              className="filter-toggle-btn"
              onClick={() => setIsFilterOpen((prev) => !prev)}
              ref={filterButtonRef}
            >
              {isFilterOpen ? "Hide Filters" : "Show Filters"}
            </button>
            {isFilterOpen &&
              dropdownPosition.top !== 0 &&
              dropdownPosition.left !== 0 && (
                <div
                  className="filter-dropdown-box"
                  ref={filterDropdownRef}
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    maxHeight: dropdownPosition.maxHeight
                      ? `${dropdownPosition.maxHeight}px`
                      : "500px",
                    overflowY: dropdownOverflow,
                    position: "fixed",
                  }}
                >
                  <div className="filter-group">
                    <label>Label Name</label>
                    <input
                      type="text"
                      placeholder="Enter Name"
                      value={filters.name || ""}
                      onChange={(e) =>
                        handleFilterChange("name", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>Description</label>
                    <input
                      type="text"
                      placeholder="Enter Description"
                      value={filters.description || ""}
                      onChange={(e) =>
                        handleFilterChange("description", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>Priority Level</label>
                    <select
                      value={filters.priorityLevel || ""}
                      onChange={(e) =>
                        handleFilterChange("priorityLevel", e.target.value)
                      }
                    >
                      <option value="">All</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Court Ref</label>
                    <input
                      type="text"
                      placeholder="Enter Court Ref"
                      value={filters.courtRef || ""}
                      onChange={(e) =>
                        handleFilterChange("courtRef", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>From Date</label>
                    <input
                      type="date"
                      value={filters.fromDate || ""}
                      onChange={(e) =>
                        handleFilterChange("fromDate", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label>To Date</label>
                    <input
                      type="date"
                      value={filters.toDate || ""}
                      onChange={(e) =>
                        handleFilterChange("toDate", e.target.value)
                      }
                    />
                  </div>

                  <div className="filter-actions">
                    <button
                      className="clear-btn"
                      onClick={() => {
                        setFilters({
                          name: "",
                          description: "",
                          priorityLevel: "",
                          courtRef: "",
                          fromDate: "",
                          toDate: "",
                        });
                        setCurrentPage(0);
                      }}
                    >
                      Clear
                    </button>
                    <button
                      className="hide-btn"
                      onClick={() => setIsFilterOpen(false)}
                    >
                      Hide
                    </button>
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* {(Object.values(filters).some(val => val) || hasLabelFilters) && (
                    <div className="applied-filters">
                        <strong>Applied Filters:</strong>
                        {labelSearchTerm && <span>Search: {labelSearchTerm}</span>}
                        {filters.name && <span>Name: {filters.name}</span>}
                        {filters.description && <span>Description: {filters.description}</span>}
                        {filters.priorityLevel && <span>Priority: {filters.priorityLevel}</span>}
                        {filters.courtRef && <span>Court Ref: {filters.courtRef}</span>}
                        {filters.fromDate && <span>From: {filters.fromDate}</span>}
                        {filters.toDate && <span>To: {filters.toDate}</span>}
                    </div>
                )} */}
        {(Object.values(filters).some((val) => val) || hasLabelFilters) && (
          <div className="applied-filters">
            <span className="filters-text">Applied Filters:</span>

            {/* Search term filter pill */}
            {labelSearchTerm && labelSearchTerm.trim() && (
              <span
                className="filter-tag"
                onClick={() => {
                  setLabelSearchTerm("");
                  // Clear DetailSection's search input
                  if (window.clearDetailSectionSearch) {
                    window.clearDetailSectionSearch();
                  }
                }}
              >
                Search: {formatText(labelSearchTerm)} ×
              </span>
            )}

            {/* Individual filter pills */}
            {filters.name && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, name: "" })}
              >
                Name: {formatText(filters.name)} ×
              </span>
            )}

            {filters.description && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, description: "" })}
              >
                Description: {formatText(filters.description)} ×
              </span>
            )}

            {filters.priorityLevel && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, priorityLevel: "" })}
              >
                Priority: {formatText(filters.priorityLevel)} ×
              </span>
            )}

            {filters.courtRef && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, courtRef: "" })}
              >
                Court Ref: {formatText(filters.courtRef)} ×
              </span>
            )}

            {filters.fromDate && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, fromDate: "" })}
              >
                From: {formatText(filters.fromDate)} ×
              </span>
            )}

            {filters.toDate && (
              <span
                className="filter-tag"
                onClick={() => setFilters({ ...filters, toDate: "" })}
              >
                To: {formatText(filters.toDate)} ×
              </span>
            )}

            <span
              className="filter-tag clear-all-tag"
              onClick={() => {
                clearLabelFilters();
                // Clear local filters
                setFilters({
                  name: "",
                  description: "",
                  priorityLevel: "",
                  courtRef: "",
                  fromDate: "",
                  toDate: "",
                });
                // Clear DetailSection's search input
                if (window.clearDetailSectionSearch) {
                  window.clearDetailSectionSearch();
                }
              }}
            >
              Clear All
            </span>
          </div>
        )}
        <div className={`label-container`}>
          {shouldShowNoDataMessage ? (
            <p className="no-notes-found">{getNoDataMessage}</p>
          ) : viewMode === "grid" ? (
            displayLabels.map((label, index) => (
              <div
                key={label._id}
                className="label-card"
                onClick={() => navigate(`/home/labels/${label._id}`)}
              >
                <div className="label-card-header">
                  <div className="label-title-section">
                    <h3 className="label-name">{formatText(label.name)}</h3>
                    <div className="label-meta">
                      <span
                        className={`priority-badge priority-${label?.priorityLevel?.toLowerCase() || "unknown"
                          }`}
                      >
                        {label.priorityLevel || "Unknown"}
                      </span>
                      <div
                        className="color-indicator"
                        style={{ backgroundColor: label.color }}
                      />
                    </div>
                  </div>

                  <div className="label-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(label);
                      }}
                      className="action-btn edit-btn"
                      title="Edit Label"
                    >
                      <BiEdit size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(label);
                      }}
                      className="action-btn delete-btn"
                      title="Delete Label"
                    >
                      <BiTrash size={16} />
                    </button>
                  </div>
                </div>

                {label.description && (
                  <div className="label-description">
                    <p>{label.description}</p>
                  </div>
                )}

                <div className="label-court-info">
                  {label.labeldata?.districtCourtCases?.length > 0 && (
                    <div className="court-section">
                      <span className="court-label">District Court:</span>
                      <div className="court-refs">
                        {label.labeldata.districtCourtCases.map(
                          (courtRef, idx) => (
                            <span
                              key={`district-${courtRef}-${idx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/home/cases/casedetails/${courtRef}`);
                              }}
                              className="court-ref-chip"
                              title={
                                caseData?.[courtRef]?.case?.parties
                                  ? WrittenFormat(
                                    caseData[courtRef].case.parties
                                  )
                                  : "Parties not available"
                              }
                            >
                              {courtRef}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {label.labeldata?.highCourtCases?.length > 0 && (
                    <div className="court-section">
                      <span className="court-label">High Court:</span>
                      <div className="court-refs">
                        {label.labeldata.highCourtCases.map((courtRef, idx) => (
                          <span
                            key={`high-${courtRef}-${idx}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/home/cases/casedetails/${courtRef}`);
                            }}
                            className="court-ref-chip"
                            title={
                              caseData?.[courtRef]?.case?.parties
                                ? WrittenFormat(caseData[courtRef].case.parties)
                                : "Parties not available"
                            }
                          >
                            {courtRef}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {label.labeldata?.otherCourtCases?.length > 0 && (
                    <div className="court-section">
                      <span className="court-label">Other Court:</span>
                      <div className="court-refs">
                        {label.labeldata.otherCourtCases.map(
                          (courtRef, idx) => (
                            <span
                              key={`other-${courtRef}-${idx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/home/cases/casedetails/${courtRef}`);
                              }}
                              className="court-ref-chip"
                              title={
                                caseData?.[courtRef]?.case?.parties
                                  ? WrittenFormat(
                                    caseData[courtRef].case.parties
                                  )
                                  : "Parties not available"
                              }
                            >
                              {courtRef}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="label-footer">
                  <span className="created-date">
                    Created {formatDate(label.createdAt)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <table className="labels-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Priority</th>
                  <th>Color</th>
                  <th>Court Refs</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayLabels.map((label, index) => (
                  <tr
                    key={index}
                    onClick={() => navigate(`/home/labels/${label._id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{label.name}</td>
                    <td style={{
                      maxWidth: "200px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}>
                      {label.description || "-"}
                    </td>
                    <td>{label.priorityLevel || "-"}</td>
                    <td>
                      <span
                        className="color-circle"
                        style={{
                          backgroundColor: label.color,
                          display: "inline-block",
                          width: "1rem",
                          height: "1rem",
                          borderRadius: "50%",
                        }}
                      />
                    </td>
                    {console.log(caseData, "caseDatacheck")}
                    <td>
                      {[
                        ...(label.labeldata?.districtCourtCases || []),
                        ...(label.labeldata?.highCourtCases || []),
                        ...(label.labeldata?.otherCourtCases || []),
                        label.courtRef,
                      ]
                        .filter(Boolean)
                        .map((courtRef, idx) => (
                          <span key={idx}>
                            <span
                              className="court-ref-hover"
                              title={
                                caseData?.[courtRef]?.case?.parties
                                  ? WrittenFormat(
                                    caseData[courtRef].case.parties
                                  )
                                  : "Parties not available"
                              }
                            // style={{
                            //     textDecoration: 'underline',
                            //     cursor: 'help',
                            //     color: '#007bff'
                            // }}
                            >
                              {courtRef}
                            </span>
                            {idx <
                              [
                                ...(label.labeldata?.districtCourtCases || []),
                                ...(label.labeldata?.highCourtCases || []),
                                ...(label.labeldata?.otherCourtCases || []),
                                label.courtRef,
                              ].filter(Boolean).length -
                              1 && ", "}
                          </span>
                        ))}
                    </td>
                    <td>{formatDate(label.createdAt)}</td>
                    <td>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(label);
                        }}
                      >
                        <BiEdit size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(label);
                        }}
                      >
                        <BiTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {(pagination.totalLabels > pagination.limit || pagination.page > 0) && (
          <div className="pagination-controls">
            <button
              disabled={pagination.page === 0}
              onClick={() => handlePageChange(0)}
            >
              First
            </button>
            <button
              disabled={pagination.page === 0}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Previous
            </button>
            <span>
              Page {pagination.page + 1} of {Math.ceil(pagination.totalLabels / pagination.limit)}
            </span>
            <button
              disabled={pagination.page >= Math.ceil(pagination.totalLabels / pagination.limit) - 1}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </button>
            <button
              disabled={pagination.page >= Math.ceil(pagination.totalLabels / pagination.limit) - 1}
              onClick={() => handlePageChange(Math.ceil(pagination.totalLabels / pagination.limit) - 1)}
            >
              Last
            </button>
          </div>
        )}
      </div>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        itemType={`label "${labelToDelete?.name || ""}"`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};
